import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/**
 * 当前登录管理员的信息。后端 /api/admin/auth/me 校验后才有值。
 * 新字段：
 * - permissions: 权限码列表（从 /me 接口拿，自动过滤空）
 * - isSuper: 是否超管（绕过权限校验）
 * - roleCodes: 所有角色 code（旧 role + RBAC）
 */
export interface AdminInfo {
  id: number
  username: string
  nickname?: string
  role?: string
  avatar?: string
  is_super?: boolean
  role_codes?: string[]
  permissions?: string[]
  org_allowed_case_types?: number[] | null
  [key: string]: unknown
}

const ADMIN_INFO_KEY = 'admin_info'

/**
 * 从 localStorage 恢复 adminInfo。
 * 不持久化时, 每次刷新 permissions 都是空集, 一旦 /auth/me 超时或 5xx,
 * 所有带 permission 的路由都会被判为"无权限"→ 跳 /403 → 用户被迫重新登录。
 * 缓存只是兜底, 每次导航仍会向 /auth/me 拉最新权限并覆盖。
 */
function loadAdminInfo(): AdminInfo | null {
  try {
    const raw = localStorage.getItem(ADMIN_INFO_KEY)
    return raw ? (JSON.parse(raw) as AdminInfo) : null
  } catch {
    localStorage.removeItem(ADMIN_INFO_KEY)
    return null
  }
}

export const useUserStore = defineStore('user', () => {
  const token = ref(localStorage.getItem('token') || '')
  const adminInfo = ref<AdminInfo | null>(loadAdminInfo())
  const tokenVerified = ref(false)

  /** 当前管理员拥有的权限码集合（computed 缓存） */
  const permissions = computed<Set<string>>(() => {
    return new Set(adminInfo.value?.permissions ?? [])
  })

  const isSuper = computed(() => Boolean(adminInfo.value?.is_super))

  const roleCodes = computed<string[]>(() => adminInfo.value?.role_codes ?? [])

  /**
   * 是否拥有某个权限码（超管直接通过）。
   * 支持多个：hasPermission(['a', 'b']) 表示拥有任一即可。
   */
  function hasPermission(code: string | string[]): boolean {
    if (isSuper.value) return true
    const codes = Array.isArray(code) ? code : [code]
    return codes.some((c) => permissions.value.has(c))
  }

  const setToken = (newToken: string) => {
    token.value = newToken
    localStorage.setItem('token', newToken)
    tokenVerified.value = false
  }

  const setAdminInfo = (info: AdminInfo | null) => {
    adminInfo.value = info
    if (info) {
      localStorage.setItem(ADMIN_INFO_KEY, JSON.stringify(info))
    } else {
      localStorage.removeItem(ADMIN_INFO_KEY)
    }
  }

  const markTokenVerified = () => {
    tokenVerified.value = true
  }

  const logout = () => {
    token.value = ''
    adminInfo.value = null
    tokenVerified.value = false
    localStorage.removeItem('token')
    localStorage.removeItem(ADMIN_INFO_KEY)
  }

  return {
    token,
    adminInfo,
    tokenVerified,
    permissions,
    isSuper,
    roleCodes,
    hasPermission,
    setToken,
    setAdminInfo,
    markTokenVerified,
    logout
  }
})